/**
 * PaceCerto — Pace Fit ("Descubra seu número") e atalhos dos painéis da PDP.
 * Porte fiel de src/lib/pace-fit.ts (Lovable). Sem dependências.
 */

const REFERENCIAS = {"Nike":{"masculino":{"34":21.6,"34.5":22,"35":22.4,"35.5":22.9,"36":23.3,"37":23.7,"37.5":24.1,"38":24.5,"39":25,"39.5":25.4,"40":25.8,"40.5":26.2,"41":26.7,"42":27.1,"42.5":27.5,"43":27.9,"43.5":28.3,"44":28.8,"45":29.2,"46":29.6},"feminino":{"33.5":22,"34":22.4,"35":22.9,"35.5":23.3,"36":23.7,"37":24.1,"37.5":24.5,"38":25,"39":25.4,"39.5":25.8,"40":26.2,"41":26.7,"41.5":27.1,"42":27.5,"43":27.9}},"adidas":{"unissex":{"33":21.6,"34":22.5,"35":22.9,"36":23.8,"37":24.2,"38":24.6,"39":25,"40":25.9,"41":26.7,"42":27.1,"43":28,"44":28.8,"45":29.3,"46":30.1,"47":31}},"ASICS":{"masculino":{"34":22.5,"35":23,"36":23.5,"37":24,"38":25.5,"39":26,"40":27,"41":27.5,"42":28.25,"43":29,"44":29.5,"45":30,"46":30.5},"feminino":{"34":22.75,"35":23.5,"36":24,"37":24.5,"38":25.5,"39":25.75,"40":26.5}},"Olympikus":{"unissex":{"34":22.7,"35":23.4,"36":24,"37":24.7,"38":25.4,"39":26,"40":26.7,"41":27.4,"42":28,"43":28.7,"44":29.4}},"New Balance":{"masculino":{"38":24.5,"39":25,"40":26,"41":26.8,"42":27,"43":28,"44":29,"45":29.8,"46":30.5,"47":31.5,"48":32.3}},"Hoka":{"masculino":{"38":25,"39":25.5,"40":26.6,"41":27,"42":28,"43":29,"44":30,"45":31,"46":32,"47":33},"feminino":{"34":22.5,"35":23,"36":24,"36.5":24.5,"37":25,"38":25.5,"39":26,"39.5":26.5,"40":27}}};

const AJUSTE = { peLargo: 0.3, provaLonga: 0.3, meiaGrossa: 0.2 };
const CHAVE_PACEFIT = 'pacefit';

const numeroBr = (valor) => String(Math.round(valor * 10) / 10).replace('.', ',');
const limite = (medida) => (Array.isArray(medida) ? medida[1] : medida);
const normalizarNumero = (valor) => String(valor ?? '').trim().replace(',', '.');

function tabelaReferencia(marca, genero) {
  const ref = REFERENCIAS[marca];
  if (!ref) return null;
  return (genero ? ref[genero] : undefined) ?? ref.unissex ?? ref.masculino ?? ref.feminino ?? null;
}

function medianaReferencia(numeracao) {
  const valores = Object.values(REFERENCIAS)
    .flatMap((marca) => [marca.masculino, marca.feminino, marca.unissex])
    .flatMap((tabela) => (tabela && tabela[numeracao] !== undefined ? [tabela[numeracao]] : []))
    .sort((a, b) => a - b);
  if (valores.length === 0) return null;
  const meio = Math.floor(valores.length / 2);
  return valores.length % 2 === 1 ? valores[meio] : (valores[meio - 1] + valores[meio]) / 2;
}

export function calcularPaceFit(entrada, ajustes, tabela) {
  let peBase = null;
  let confianca = 'baixa';

  if (entrada.modo === 'pe') {
    peBase = entrada.cm;
    confianca = 'alta';
  } else if (entrada.modo === 'referencia') {
    const ref = tabelaReferencia(entrada.marca, entrada.genero)?.[entrada.numeracao];
    if (ref !== undefined) {
      peBase = ref + (entrada.caimento === 'justo' ? 0.3 : entrada.caimento === 'folgado' ? -0.3 : 0);
    }
    confianca = 'media';
  } else {
    peBase = medianaReferencia(entrada.numeracao);
  }

  if (peBase === null) {
    return { numeracao: null, confianca, motivo: 'Não reconhecemos essa numeração. Confira o número ou use outra opção.' };
  }

  const acrescimo =
    (ajustes.peLargo ? AJUSTE.peLargo : 0) + (ajustes.provaLonga ? AJUSTE.provaLonga : 0) + (ajustes.meiaGrossa ? AJUSTE.meiaGrossa : 0);
  const peAjustado = Math.round((peBase + acrescimo) * 100) / 100;
  const ordenada = Object.entries(tabela).sort((a, b) => Number(a[0]) - Number(b[0]));
  const indice = ordenada.findIndex(([, medida]) => limite(medida) >= peAjustado);

  if (indice < 0) {
    return { numeracao: null, confianca, motivo: 'Este modelo não vai até o seu tamanho.' };
  }

  const candidata = ordenada[indice];
  const fronteira = limite(candidata[1]) - peAjustado < 0.2;
  const escolhida = fronteira && ordenada[indice + 1] ? ordenada[indice + 1] : candidata;

  return {
    numeracao: escolhida[0],
    medida: escolhida[1],
    peAjustado,
    confianca,
    motivo:
      fronteira && escolhida !== candidata
        ? 'Sua medida ficou no limite; indicamos a próxima numeração.'
        : acrescimo > 0
          ? 'A recomendação considera os ajustes selecionados.'
          : 'Esta é a menor numeração que comporta sua medida.',
  };
}

const prefereMenosMovimento = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function abrirPainel(id) {
  const alvo = document.getElementById(id);
  if (!alvo) return false;
  if (alvo instanceof HTMLDetailsElement) alvo.open = true;
  alvo.scrollIntoView({ behavior: prefereMenosMovimento() ? 'auto' : 'smooth', block: 'start' });
  const foco = alvo.querySelector('summary') ?? alvo;
  foco.focus({ preventScroll: true });
  return true;
}

if (!window.__pcPainelDelegado) {
  window.__pcPainelDelegado = true;
  document.addEventListener('click', (evento) => {
    const gatilho = evento.target instanceof Element ? evento.target.closest('[data-pc-abrir]') : null;
    if (!gatilho) return;
    if (abrirPainel(gatilho.getAttribute('data-pc-abrir'))) evento.preventDefault();
  });
}

class PcPaceFit extends HTMLElement {
  connectedCallback() {
    if (this.dados) return;
    try {
      this.dados = JSON.parse(this.querySelector('script[data-pc-dados]')?.textContent ?? '{}');
    } catch {
      this.dados = {};
    }
    // PDP: uma tabela (dados.tabela). Página de numeração: várias (dados.tabelas = [{ titulo, url, tabela }]).
    if (!this.dados.tabela && !Array.isArray(this.dados.tabelas)) return;

    this.form = this.querySelector('form');
    this.resultado = this.querySelector('[data-resultado]');
    this.selectMarca = this.form.elements.namedItem('marca');
    this.selectGenero = this.form.elements.namedItem('genero');
    this.selectNumeroRef = this.form.elements.namedItem('numero_ref');

    for (const marca of Object.keys(REFERENCIAS)) {
      this.selectMarca.add(new Option(marca, marca));
    }

    this.restaurar();
    this.atualizarReferencia(false);
    this.mostrarModo();
    this.calcular();

    this.form.addEventListener('submit', (evento) => evento.preventDefault());
    this.form.addEventListener('input', (evento) => this.aoMudar(evento));
    this.form.addEventListener('change', (evento) => this.aoMudar(evento));
    this.resultado.addEventListener('click', (evento) => {
      const botao = evento.target instanceof Element ? evento.target.closest('[data-usar]') : null;
      if (botao) this.usarNumero(botao.getAttribute('data-usar'));
    });
  }

  get modo() {
    return this.form.elements.namedItem('modo').value || 'pe';
  }

  aoMudar(evento) {
    const nome = evento.target?.name;
    if (nome === 'modo') this.mostrarModo();
    if (nome === 'marca' || nome === 'genero') this.atualizarReferencia(nome === 'marca');
    this.calcular();
  }

  mostrarModo() {
    for (const painel of this.querySelectorAll('[data-modo]')) {
      painel.hidden = painel.getAttribute('data-modo') !== this.modo;
    }
  }

  atualizarReferencia(trocouMarca) {
    const ref = REFERENCIAS[this.selectMarca.value] ?? {};
    const temGeneros = Boolean(ref.masculino && ref.feminino);
    const campoGenero = this.querySelector('[data-campo-genero]');
    if (campoGenero) campoGenero.hidden = !temGeneros;
    if (trocouMarca) this.selectGenero.value = ref.masculino ? 'masculino' : 'feminino';

    const tabela = tabelaReferencia(this.selectMarca.value, temGeneros ? this.selectGenero.value : undefined) ?? {};
    const atual = this.selectNumeroRef.value;
    this.selectNumeroRef.length = 1;
    for (const numero of Object.keys(tabela).sort((a, b) => Number(a) - Number(b))) {
      this.selectNumeroRef.add(new Option(numero.replace('.', ','), numero));
    }
    this.selectNumeroRef.value = tabela[atual] !== undefined && !trocouMarca ? atual : '';
  }

  lerEntrada() {
    const f = this.form.elements;
    if (this.modo === 'pe') {
      const cm = Number(normalizarNumero(f.namedItem('cm').value));
      return Number.isFinite(cm) && cm >= 15 && cm <= 40 ? { modo: 'pe', cm } : null;
    }
    if (this.modo === 'referencia') {
      const marca = f.namedItem('marca').value;
      const ref = REFERENCIAS[marca] ?? {};
      const temGeneros = Boolean(ref.masculino && ref.feminino);
      const genero = temGeneros ? f.namedItem('genero').value : undefined;
      const numeracao = f.namedItem('numero_ref').value;
      if (!marca || !numeracao || tabelaReferencia(marca, genero)?.[numeracao] === undefined) return null;
      return { modo: 'referencia', marca, ...(genero ? { genero } : {}), numeracao, caimento: f.namedItem('caimento').value || 'certo' };
    }
    const numeracao = normalizarNumero(f.namedItem('numero').value);
    return numeracao ? { modo: 'numero', numeracao } : null;
  }

  lerAjustes() {
    const f = this.form.elements;
    return { peLargo: f.namedItem('pe_largo').checked, provaLonga: f.namedItem('prova_longa').checked, meiaGrossa: f.namedItem('meia_grossa').checked };
  }

  calcular() {
    const entrada = this.lerEntrada();
    const ajustes = this.lerAjustes();
    if (!entrada) {
      this.resultado.replaceChildren();
      return;
    }
    try {
      window.localStorage.setItem(CHAVE_PACEFIT, JSON.stringify({ entrada, ajustes }));
    } catch {
      /* armazenamento indisponível: a calculadora segue funcionando */
    }
    if (Array.isArray(this.dados.tabelas)) this.renderizarTabelas(entrada, ajustes);
    else this.renderizar(calcularPaceFit(entrada, ajustes, this.dados.tabela));
  }

  /** Página de numeração: número recomendado em cada marca ou modelo (ResultadoPaceFitCompleto do Lovable). */
  renderizarTabelas(entrada, ajustes) {
    const tabela = document.createElement('table');
    tabela.className = 'pc-tabela';
    const legenda = document.createElement('caption');
    legenda.className = 'pc-visually-hidden';
    legenda.textContent = 'Número recomendado por marca e modelo';
    const cabeca = document.createElement('thead');
    cabeca.innerHTML = '<tr><th scope="col">Marca ou modelo</th><th scope="col">Número recomendado</th><th scope="col">Pé até</th></tr>';
    const corpo = document.createElement('tbody');
    for (const item of this.dados.tabelas) {
      const r = calcularPaceFit(entrada, ajustes, item.tabela || {});
      const linha = document.createElement('tr');
      const nome = document.createElement('th');
      nome.scope = 'row';
      if (item.url) {
        const link = document.createElement('a');
        link.href = item.url;
        link.textContent = item.titulo;
        nome.append(link);
      } else {
        nome.textContent = item.titulo;
      }
      const numero = document.createElement('td');
      numero.className = 'pc-num pc-numeracao-resultado__numero';
      numero.textContent = r.numeracao ? r.numeracao.replace('.', ',') : '—';
      const medida = document.createElement('td');
      medida.className = 'pc-num';
      medida.textContent = r.medida ? `${numeroBr(limite(r.medida))} cm` : r.numeracao ? '—' : 'fora da tabela';
      linha.append(nome, numero, medida);
      corpo.append(linha);
    }
    tabela.append(legenda, cabeca, corpo);
    const rolagem = document.createElement('div');
    rolagem.className = 'pc-tabela-rolagem pc-numeracao-resultado';
    rolagem.append(tabela);
    this.resultado.replaceChildren(rolagem);
  }

  restaurar() {
    let salvo = null;
    try {
      salvo = JSON.parse(window.localStorage.getItem(CHAVE_PACEFIT) ?? 'null');
    } catch {
      salvo = null;
    }
    if (!salvo?.entrada) return;
    const f = this.form.elements;
    const { entrada, ajustes = {} } = salvo;
    const radioModo = this.form.querySelector(`input[name="modo"][value="${entrada.modo}"]`);
    if (radioModo) radioModo.checked = true;
    if (entrada.modo === 'pe') f.namedItem('cm').value = String(entrada.cm).replace('.', ',');
    if (entrada.modo === 'numero') f.namedItem('numero').value = String(entrada.numeracao).replace('.', ',');
    if (entrada.modo === 'referencia' && REFERENCIAS[entrada.marca]) {
      this.selectMarca.value = entrada.marca;
      if (entrada.genero) this.selectGenero.value = entrada.genero;
      this.atualizarReferencia(false);
      this.selectNumeroRef.value = entrada.numeracao;
      const radioCaimento = this.form.querySelector(`input[name="caimento"][value="${entrada.caimento}"]`);
      if (radioCaimento) radioCaimento.checked = true;
    }
    f.namedItem('pe_largo').checked = Boolean(ajustes.peLargo);
    f.namedItem('prova_longa').checked = Boolean(ajustes.provaLonga);
    f.namedItem('meia_grossa').checked = Boolean(ajustes.meiaGrossa);
  }

  corAtual() {
    const picker = this.closest('.shopify-section, main') ?? document;
    return picker.querySelector('.variant-option--pc-cor input:checked')?.value ?? null;
  }

  disponivelNaCor(numeracao) {
    const { variantes = [], posCor = -1, posNumeracao = -1 } = this.dados;
    if (posNumeracao < 0) return false;
    const cor = this.corAtual();
    return variantes.some(
      (v) => v.d && normalizarNumero(v.o[posNumeracao]) === numeracao && (posCor < 0 || cor === null || v.o[posCor] === cor),
    );
  }

  renderizar(r) {
    const bloco = document.createElement('div');
    bloco.className = 'pc-resultado';
    if (!r.numeracao) {
      const p = document.createElement('p');
      p.className = 'pc-resultado__motivo';
      p.textContent = r.motivo;
      bloco.append(p);
      this.resultado.replaceChildren(bloco);
      return;
    }

    const rotuloConfianca = { alta: 'Confiança alta', media: 'Confiança média', baixa: 'Confiança baixa' }[r.confianca];
    const medida = Array.isArray(r.medida) ? `${numeroBr(r.medida[0])}–${numeroBr(r.medida[1])}` : numeroBr(r.medida);
    const numeroExibido = r.numeracao.replace('.', ',');

    const topo = document.createElement('p');
    topo.className = 'pc-resultado__numero';
    const forte = document.createElement('strong');
    forte.textContent = numeroExibido;
    topo.append(forte, ` · pé até ${medida} cm`);

    const selo = document.createElement('span');
    selo.className = `pc-selo pc-selo--${r.confianca}`;
    selo.textContent = rotuloConfianca;

    const motivo = document.createElement('p');
    motivo.className = 'pc-resultado__motivo';
    motivo.textContent = r.motivo;

    bloco.append(topo, selo, motivo);

    if (this.disponivelNaCor(r.numeracao)) {
      const botao = document.createElement('button');
      botao.type = 'button';
      botao.className = 'pc-button pc-button--secundario';
      botao.setAttribute('data-usar', r.numeracao);
      botao.textContent = `Usar ${numeroExibido}`;
      bloco.append(botao);
    } else {
      const aviso = document.createElement('p');
      aviso.className = 'pc-resultado__aviso';
      aviso.textContent = `${numeroExibido} indisponível nesta cor.`;
      bloco.append(aviso);
    }
    this.resultado.replaceChildren(bloco);
  }

  usarNumero(numeracao) {
    const escopo = this.closest('.shopify-section, main') ?? document;
    const inputs = escopo.querySelectorAll('.variant-option--pc-numeracao input[type="radio"]');
    const alvo = Array.from(inputs).find((input) => normalizarNumero(input.value) === numeracao);
    if (!alvo) return;
    if (!alvo.checked) alvo.click();
    const fieldset = alvo.closest('fieldset');
    fieldset?.scrollIntoView({ behavior: prefereMenosMovimento() ? 'auto' : 'smooth', block: 'center' });
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = `Numeração ${numeracao.replace('.', ',')} selecionada.`;
  }
}

if (!customElements.get('pc-pace-fit')) customElements.define('pc-pace-fit', PcPaceFit);
